import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, Button, Grid, IconButton, Popover, Typography, Alert, Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Info, LibraryBooks, TextSnippet } from '@mui/icons-material';
import { pdfjs, Document, Page } from 'react-pdf';
import { checkFile, CloseIconButton, ConditionalWrapper, fetchFile, trackEvent, useWindowDimensions } from '../Utilities';
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";
import { isMobile, useMobileOrientation } from 'react-device-detect';
import dayjs from 'dayjs';
import DatePicker from '../DatePicker';
import CameraSelection from '../CameraSelection';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`

export default function Diary({ timestamp, setTimestamp, loading, disabled, shouldDisableDate, siderWidth, footerOpen, setFooterOpen }) {
    
    const { CAMERAS, LAYOUT: { TITLE }, BASEURL } = window.conf;
    const { t } = useTranslation();
    const { contentHeight, windowWidth } = useWindowDimensions()
    const { isPortrait } = useMobileOrientation();

    const [cameraId, setCameraId] = useState(CAMERAS?.length ? CAMERAS[0].id : 0);
    const [open, setOpen] = useState(false);
    const [anchorPopover, setAnchorPopover] = useState(null);
    const [numPages, setNumPages] = useState(null);
    const [pdfExists, setPdfExists] = useState(false);
    const [alertMessage, setAlertMessage] = useState(null);

    const openPopover = Boolean(anchorPopover)
    const idPopover = openPopover ? 'simple-popover' : undefined
    const moreThanOneCam = CAMERAS.length > 1
    const selectedCam = CAMERAS.find(camera => camera.id === cameraId)
    const selectedDate = timestamp.format("YY-MM-DD")
    const maxWindowWidth = 900
    const contentOffset = { 
        height: 3 * 16 + 56 + (moreThanOneCam && 72), // 3 * 16px padding + 56px button height and tolerance + 72px camera selector
        width: 2 * 16 + (windowWidth > maxWindowWidth ? 24 : 84) // 2 * 16px padding + 24px or 84px tolerance
    }
    const staticReport = false
    const reportPath = staticReport ? `https://pdfobject.com/pdf/sample.pdf` : `${BASEURL}cam${selectedCam.id + 1}/reports/${selectedDate}.pdf`
    const pdfSource = useMemo(() => ({ url: reportPath }), [reportPath]);
    const today = dayjs()
    const title = TITLE ? `${TITLE}_` : ""
    const camera = selectedCam.name 
        ? `${selectedCam.name}_` 
        : moreThanOneCam 
            ? `${t("events.camera")} ${selectedCam.id + 1}_` 
            : ""
    const targetFilename = `${title}${camera}${selectedDate}` || t("events.construction_diary.construction_diary")

    useEffect(() => {
        const checkPdf = async() => {
            const { exists, contentType } = await checkFile(pdfSource.url)
            if (exists && contentType === "pdf") {
                setAlertMessage(null)
                setPdfExists(true)
            } else {
                if (today.isSame(timestamp, 'day')) {
                    setAlertMessage({ type: 'warning', message: t('events.construction_diary.no_data_today') })
                } else {
                    setAlertMessage({ type: 'error', message: t('msg.no_file') })
                }
                setPdfExists(false)
            }
        }
        checkPdf()
    }, [pdfSource.url, timestamp]) // eslint-disable-line react-hooks/exhaustive-deps

    const onOpenDialog = () => {
        setOpen(true)
        trackEvent(t("events.construction_diary.construction_diary", { lng: "de" }), t("actions.create", { lng: "de" }))
    }

    const onCloseDialog = () => {
        setOpen(false)
    }

    const onOpenPopover = (event) => {
        setAnchorPopover(event.currentTarget)
    }
    
    const onClosePopover = () => {
        setAnchorPopover(null)
    }

    function onOpenDrawer() {
        if (setFooterOpen && !footerOpen) {
            setFooterOpen()
        }
    } 

    // disables panning when image is not zoomed in or panned
    const PanningInteractionHandler = (instance, state) => {
        if (state?.positionX >= 0 && state?.positionY >= 0 && state?.scale <= 1) {
            instance.setup.panning.disabled = true;
        } else {
            instance.setup.panning.disabled = false;
        }
    }

    const pdfDocument = (
        <Document 
            file={pdfSource}
            loading={t('actions.loading')}
            onLoadSuccess={({ numPages }) => setNumPages(numPages)}
            error={t('msg.exe_error')}
            onLoadError={error => console.error(`[Diary] PDF load error`, error)}
            noData={t('msg.no_data')}
        >

            {[...Array(numPages)].map((_, index) => (
                <Page
                    key={`page_${index + 1}`}
                    pageNumber={index + 1}
                    width={(windowWidth > maxWindowWidth ? maxWindowWidth : windowWidth) - contentOffset.width}
                />
            ))}

        </Document>
    )

    return (
        <>
            {isMobile && isPortrait && !footerOpen ? (
                <IconButton
                    area-label="Construction Diary"
                    color="primary"
                    onClick={onOpenDrawer}
                    sx={{ pointerEvents: "all" }}
                    disabled={loading || disabled}
                >
                    <TextSnippet sx={{ fontSize: "2rem" }} />
                </IconButton>
            ) : (
                <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>

                    {moreThanOneCam && (
                        <CameraSelection
                            value={cameraId}
                            onChange={setCameraId}
                        />
                    )}

                    <DatePicker
                        timestamp={timestamp}
                        setTimestamp={setTimestamp}
                        loading={loading}
                        shouldDisableDate={shouldDisableDate}
                        siderWidth={siderWidth}
                        pb={2}
                        withoutToday
                    />

                    <ConditionalWrapper
                        condition={timestamp.isSame(today, 'day')}
                        wrapper={children => <Tooltip title={t("events.construction_diary.no_data_today")} placement="top" arrow>{children}</Tooltip>}
                    >
                        <Box component="span">
                            <Button
                                onClick={onOpenDialog}
                                startIcon={<LibraryBooks />}
                                disabled={loading || disabled || timestamp.isSame(today, 'day')}
                                variant="contained"
                                size='large'
                                fullWidth
                            >
                                {t("actions.create")}
                            </Button>
                        </Box>
                    </ConditionalWrapper>

                </Box>
            )}

            <Dialog 
                open={open} 
                onClose={onCloseDialog}
                sx={{ "& .MuiDialog-paper": { maxWidth: windowWidth > maxWindowWidth ? maxWindowWidth : windowWidth }}}
            >
                <CloseIconButton onClick={onCloseDialog} />

                <DialogContent sx={{ p: 2 }}>

                    <Grid container>

                        <Grid item xs={12} sx={{ pb: 0 }} /> {/* An empty grid item. A trick to fix the lable layout issue on FormControl */}

                        {pdfExists && (
                            <Grid 
                                item 
                                xs={12} 
                                sx={{ 
                                    mb: 2,
                                    border: '1px solid',
                                    borderColor: 'rgba(196,196,196)',
                                    borderRadius: '4px',
                                    height: contentHeight - contentOffset.height,
                                    overflow: 'auto'
                                }}
                            >

                                {isMobile ? (
                                    <TransformWrapper
                                        onZoomStop={ref => PanningInteractionHandler(ref.instance, ref.state)}
                                        onPanning={ref => PanningInteractionHandler(ref.instance, ref.state)}
                                        onInit={ref => ref.instance.setup.panning.disabled = true}
                                        disablePadding // removes paddable border around the content
                                    >
                                        <TransformComponent>
                                            {pdfDocument}
                                        </TransformComponent>
                                    </TransformWrapper> 
                                ) : (
                                    pdfDocument
                                )}

                            </Grid>
                        )}

                        {alertMessage && (
                             <Grid item xs={12} sx={{ pb: 2 }}>
                                <Alert
                                    variant="filled"
                                    severity={alertMessage.type}
                                    sx={{ alignItems: "center", userSelect: "none" }}
                                >
                                    {alertMessage.message || t("msg.unknown_error")}
                                </Alert>
                            </Grid>
                        )}

                        <Grid
                            item 
                            xs={12}
                            sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between'
                            }}
                        >
                            <Button onClick={onCloseDialog} variant="outlined">
                                {t('actions.close')}
                            </Button>

                            <IconButton
                                aria-label={idPopover}
                                onClick={onOpenPopover}
                                color="primary"
                            >
                                <Info />
                            </IconButton>
                             
                            <Button 
                                variant="outlined" 
                                onClick={() => fetchFile(pdfSource.url, `${targetFilename}.pdf`)}
                                disabled={!pdfExists || !numPages}
                            >
                                {t('actions.download')}
                            </Button>

                        </Grid>

                    </Grid>
                </DialogContent>
            </Dialog>

            <Popover
                id={idPopover}
                open={openPopover}
                anchorEl={anchorPopover}
                onClose={onClosePopover}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
            >
                <Typography sx={{ 
                    p: 2, 
                    whiteSpace: "pre-line", 
                    userSelect: "none" 
                }}>
                    {t('events.construction_diary.info')}
                </Typography>
            </Popover>
        </>
    )
}